import { useMemo, useState } from "react";
import {
  api,
  type Classification,
  type InboxItem,
  type InboxStatus,
  type IngestResult,
  type IngestionKind,
  type MemoryEntry,
  type MemoryRevision,
  type EngagementParts,
  type SourceRecord,
  type Suggestion,
} from "../lib/api";
import { useEngagementHeader, useSession } from "../lib/session";
import { type Resource, useAction, useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import {
  Apparatus,
  MarginNote,
  Rail,
  RegisterLayout,
  Region,
  type RailKey,
} from "../components/Register";
import {
  AbsentNotice,
  Button,
  Empty,
  Failed,
  Field,
  Figure,
  Mark,
  Panel,
  Select,
  SkeletonRows,
  TextArea,
  classificationTone,
  ClassificationSelect,
} from "../components/primitives";
import { classificationLabel, day, humanise, stamp } from "../lib/format";

/**
 * Delivery is the hand-off between something arriving and the engagement
 * deciding it knows it. The screen keeps those facts separate:
 *
 *   Inbox    proposals, inert until a person decides
 *   Memory   accepted knowledge, with every revision retained
 *   Arrivals what was filed, by whom, and how many proposals it produced
 *
 * Nothing here turns extracted text into a task automatically. That boundary
 * is the product, not an implementation detail, so the interface states it.
 */
export function Delivery({ engagementId }: { engagementId: string }) {
  const { me } = useSession();
  const engagement = useEngagementHeader(engagementId);
  const [filter, setFilter] = useState<InboxStatus | "all">("pending");
  const [section, setSection] = useState("inbox");

  const inbox = useResource<InboxItem[]>(
    () => api.get<InboxItem[]>(`/api/engagements/${engagementId}/inbox?status=${filter}`),
    [engagementId, filter],
  );
  const pendingInbox = useResource<InboxItem[]>(
    () => api.get<InboxItem[]>(`/api/engagements/${engagementId}/inbox?status=pending`),
    [engagementId],
  );
  const memory = useResource<MemoryEntry[]>(
    () => api.get<MemoryEntry[]>(`/api/engagements/${engagementId}/memory`),
    [engagementId],
  );
  const arrivals = useResource<SourceRecord[]>(
    () => api.get<SourceRecord[]>(`/api/engagements/${engagementId}/arrivals`),
    [engagementId],
  );
  // Needed by the composer, so an arrival can be tagged to the stream it
  // bears on. Cheap, and it is the same endpoint Work and Setup use.
  const parts = useResource<EngagementParts>(
    () => api.get<EngagementParts>(`/api/engagements/${engagementId}/parts`),
    [engagementId],
  );


  const canManage = Boolean(me && me.role !== "consultant");
  const inboxCount = inbox.resource.state === "ready" ? inbox.resource.data.length : 0;
  const pendingCount =
    pendingInbox.resource.state === "ready" ? pendingInbox.resource.data.length : 0;
  const memoryCount = memory.resource.state === "ready" ? memory.resource.data.length : 0;
  const arrivalCount = arrivals.resource.state === "ready" ? arrivals.resource.data.length : 0;

  const railKeys = useMemo<RailKey[]>(
    () =>
      [
        { id: "arrivals", key: "NT", label: `Notes (${arrivalCount})` },
        ...(inboxCount > 0
          ? [{ id: "inbox", key: "RV", label: `Items to review (${inboxCount})` }]
          : []),
        ...(memoryCount > 0
          ? [{ id: "memory", key: "ER", label: `Engagement record (${memoryCount})` }]
          : []),
      ].map((key) => ({
        ...key,
        state: key.id === section ? ("live" as const) : ("rest" as const),
        onSelect: () => {
          setSection(key.id);
          document.getElementById(`region-${key.id}`)?.scrollIntoView({
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
              ? "auto"
              : "smooth",
            block: "start",
          });
        },
      })),
    [arrivalCount, inboxCount, memoryCount, section],
  );

  const reloadKnowledge = () => {
    inbox.reload();
    pendingInbox.reload();
    memory.reload();
  };

  const reloadIntake = () => {
    arrivals.reload();
    inbox.reload();
    pendingInbox.reload();
  };

  return (
    <AppShell engagement={engagement ?? undefined}>
      <ScreenHead
        title="Notes"
        sub="What was said, by whom, and when. Write it down here and it stays on the engagement."
      />

      <RegisterLayout
        rail={<Rail keys={railKeys} />}
        margin={
          <Apparatus>
            <div className="flex gap-7 xl:gap-8">
              <Figure
                value={pendingCount}
                label="needs review"
                tone={pendingCount > 0 ? "warning" : "muted"}
              />
              <Figure value={memoryCount} label="engagement records" />
            </div>
            <MarginNote title="Review before saving">
              New information is added to the engagement record only after a
              person reviews and approves it.
            </MarginNote>
            {!canManage && (
              <MarginNote title="Your access">
                You can add and read information available to you. A Lead
                approves new entries and later changes.
              </MarginNote>
            )}
          </Apparatus>
        }
      >
        {/* The order is the pipeline, and it used to run backwards.
            "Items to review" and "Project record" are both empty on a new
            engagement, and the one control that fills them — the composer
            — sat underneath both. So you arrived at two empty registers
            and had to scroll past them to find the only thing you could
            do. Add, then review, then the record: each section is fed by
            the one above it. */}
        <div id="region-arrivals">
          <ArrivalsRegion
            engagementId={engagementId}
            resource={arrivals.resource}
            workstreams={
              parts.resource.state === "ready" ? parts.resource.data.workstreams : []
            }
            // Default to the lowest until the real one arrives: offering
            // too much and then taking it away is worse than the reverse.
            ceiling={
              parts.resource.state === "ready"
                ? parts.resource.data.ceiling
                : "client_visible"
            }
            onRetry={arrivals.reload}
            onChanged={reloadIntake}
          />
        </div>

        {/* These two are stages of the extraction pipeline, not places a
            person writes anything. On an engagement where nothing has
            been extracted they are empty forever — and they were rendering
            as two large empty registers either side of the only part that
            works, which is what "it does nothing" meant. They appear when
            they hold something; a single line says when that is. */}
        {inboxCount > 0 && (
          <div id="region-inbox">
            <InboxRegion
              resource={inbox.resource}
              filter={filter}
              setFilter={setFilter}
              canManage={canManage}
              onRetry={inbox.reload}
              onChanged={reloadKnowledge}
            />
          </div>
        )}

        {memoryCount > 0 && (
          <div id="region-memory">
            <MemoryRegion
              resource={memory.resource}
              canManage={canManage}
              onRetry={memory.reload}
              onChanged={memory.reload}
            />
          </div>
        )}

        {inboxCount === 0 && memoryCount === 0 && arrivalCount > 0 && (
          <p className="mt-10 max-w-[62ch] border-t border-line pt-5 text-sm text-secondary">
            Nothing is waiting to be reviewed. When a note is read and
            something in it should become part of the engagement&rsquo;s
            permanent record, that suggestion appears here for a Lead to
            approve or dismiss. Approved information is saved in the
            engagement record.
          </p>
        )}
      </RegisterLayout>
    </AppShell>
  );
}

// --------------------------------------------------------------- inbox ----

function InboxRegion({
  resource,
  filter,
  setFilter,
  canManage,
  onRetry,
  onChanged,
}: {
  resource: Resource<InboxItem[]>;
  filter: InboxStatus | "all";
  setFilter: (value: InboxStatus | "all") => void;
  canManage: boolean;
  onRetry: () => void;
  onChanged: () => void;
}) {
  return (
    <Region
      title="Items to review"
      count={resource.state === "ready" ? resource.data.length : undefined}
      action={<InboxFilters value={filter} onChange={setFilter} />}
      tone={filter === "pending" && resource.state === "ready" && resource.data.length > 0 ? "attention" : "rest"}
    >
      {resource.state === "loading" && <SkeletonRows rows={4} />}
      {resource.state === "absent" && <AbsentNotice />}
      {resource.state === "failed" && (
        <Failed
          message={resource.message}
          correlationId={resource.correlationId}
          onRetry={onRetry}
        />
      )}
      {resource.state === "ready" && resource.data.length === 0 && (
        <Empty title={filter === "pending" ? "Nothing needs review" : `No ${filter} items`}>
          {filter === "pending"
            ? "Anything suggested from the information above waits here until a Lead decides whether it belongs in the engagement record."
            : "A decision stays in the register once it has been made — including the ones that went the other way."}
        </Empty>
      )}
      {resource.state === "ready" &&
        resource.data.map((item) => (
          <InboxRecord
            key={item.id}
            item={item}
            canManage={canManage}
            onChanged={onChanged}
          />
        ))}
    </Region>
  );
}

function InboxFilters({
  value,
  onChange,
}: {
  value: InboxStatus | "all";
  onChange: (value: InboxStatus | "all") => void;
}) {
  const filters: Array<{ value: InboxStatus | "all"; label: string }> = [
    { value: "pending", label: "Needs review" },
    { value: "approved", label: "Added" },
    { value: "rejected", label: "Dismissed" },
    { value: "all", label: "All" },
  ];

  return (
    <div className="flex flex-wrap" aria-label="Inbox status">
      {filters.map((filter) => (
        <button
          key={filter.value}
          type="button"
          aria-pressed={value === filter.value}
          onClick={() => onChange(filter.value)}
          className={
            "border px-2.5 py-1 text-2xs uppercase tracking-chip transition-colors duration-150 " +
            (value === filter.value
              ? "border-ink bg-ink text-canvas"
              : "border-line bg-transparent text-muted hover:border-line-strong hover:text-ink")
          }
        >
          {filter.label}
        </button>
      ))}
    </div>
  );
}

function InboxRecord({
  item,
  canManage,
  onChanged,
}: {
  item: InboxItem;
  canManage: boolean;
  onChanged: () => void;
}) {
  const suggestion = suggestionOf(item.extracted);
  const statusTone =
    item.status === "approved" ? "success" : item.status === "rejected" ? "spent" : "warning";

  return (
    <article className="register-entry py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 max-w-[62ch] flex-1">
          <p className="text-base leading-[1.3rem] text-ink">
            {suggestion?.summary ?? "New information is ready for review"}
          </p>
          <p className="mt-1 text-xs text-muted">
            Filed by {item.createdByName} &middot; {item.sourceKind ? humanise(item.sourceKind) : "source not visible"}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Mark tone={classificationTone(item.classification)}>
            {classificationLabel(item.classification)}
          </Mark>
          <Mark tone={statusTone}>{humanise(item.status)}</Mark>
        </div>
      </div>

      {suggestion?.quote && (
        <blockquote className="mt-3 max-w-[64ch] border-l border-line-strong pl-3 text-sm leading-[1.25rem] text-secondary">
          &ldquo;{suggestion.quote}&rdquo;
        </blockquote>
      )}

      {suggestion && (
        <p className="mt-2 text-2xs uppercase tracking-chip text-muted">
          {humanise(suggestion.kind)} &middot; {suggestion.confidence} confidence
          {suggestion.on && <> &middot; {day(suggestion.on)}</>}
        </p>
      )}

      {item.reviewedAt && (
        <p className="mt-3 text-xs text-muted">
          Decided by {item.reviewedByName ?? "the engagement team"} &middot; {stamp(item.reviewedAt)}
        </p>
      )}

      {item.status === "pending" && canManage && (
        <InboxDecision item={item} suggestion={suggestion} onChanged={onChanged} />
      )}
    </article>
  );
}

function InboxDecision({
  item,
  suggestion,
  onChanged,
}: {
  item: InboxItem;
  suggestion: Suggestion | null;
  onChanged: () => void;
}) {
  const [mode, setMode] = useState<"approve" | "reject" | null>(null);
  const [title, setTitle] = useState(suggestion?.summary ?? "");
  const [body, setBody] = useState(suggestion?.quote ?? suggestion?.summary ?? "");

  const decide = useAction(async (decision: "approved" | "rejected") => {
    await api.post<{ entryId: string | null }>(`/api/inbox/${item.id}/review`,
      decision === "approved" ? { decision, title, body } : { decision });
  });

  const submit = async (decision: "approved" | "rejected") => {
    const ok = await decide.invoke(decision);
    if (ok) {
      setMode(null);
      onChanged();
    }
  };

  return (
    <div className="mt-4 border-t border-line pt-3">
      {mode === null && (
        <div className="flex flex-wrap gap-2">
          <Button tone="primary" onClick={() => setMode("approve")}>
            Add to engagement record
          </Button>
          <Button tone="quiet" onClick={() => setMode("reject")}>
            Dismiss
          </Button>
        </div>
      )}

      {mode === "approve" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Field
            label="Engagement record title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <div className="lg:row-span-2">
            <TextArea
              label="How it will appear"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              className="min-h-32"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 self-end">
            <Button
              tone="primary"
              loading={decide.pending}
              disabled={!title.trim() || !body.trim()}
              onClick={() => void submit("approved")}
            >
              Add to engagement record
            </Button>
            <Button tone="quiet" disabled={decide.pending} onClick={() => setMode(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {mode === "reject" && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="max-w-[54ch] text-xs leading-[1.1rem] text-secondary">
            Dismissal is recorded. The original source remains available, but
            nothing is added to the engagement record.
          </p>
          <div className="flex gap-2">
            <Button
              tone="danger"
              loading={decide.pending}
              onClick={() => void submit("rejected")}
            >
              Dismiss item
            </Button>
            <Button tone="quiet" disabled={decide.pending} onClick={() => setMode(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {decide.refusal && <ActionRefusal refusal={decide.refusal} />}
    </div>
  );
}

// -------------------------------------------------------------- memory ----

function MemoryRegion({
  resource,
  canManage,
  onRetry,
  onChanged,
}: {
  resource: Resource<MemoryEntry[]>;
  canManage: boolean;
  onRetry: () => void;
  onChanged: () => void;
}) {
  return (
    <Region
      title="Engagement record"
      count={resource.state === "ready" ? resource.data.length : undefined}
    >
      {resource.state === "loading" && <SkeletonRows rows={4} />}
      {resource.state === "absent" && <AbsentNotice />}
      {resource.state === "failed" && (
        <Failed
          message={resource.message}
          correlationId={resource.correlationId}
          onRetry={onRetry}
        />
      )}
      {resource.state === "ready" && resource.data.length === 0 && (
        <Empty title="Nothing has been added yet">
          Approved information appears here with its original source and any
          later corrections. Earlier wording stays in the history.
        </Empty>
      )}
      {resource.state === "ready" &&
        resource.data.map((entry) => (
          <MemoryRecord
            key={entry.id}
            entry={entry}
            canManage={canManage}
            onChanged={onChanged}
          />
        ))}
    </Region>
  );
}

function MemoryRecord({
  entry,
  canManage,
  onChanged,
}: {
  entry: MemoryEntry;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <article className="register-entry py-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 max-w-[62ch] flex-1">
          <h3 className="font-display text-md font-medium tracking-title text-ink">
            {entry.title}
          </h3>
          {entry.body && (
            <p className="mt-2 whitespace-pre-wrap text-base leading-[1.35rem] text-secondary">
              {entry.body}
            </p>
          )}
          <p className="mt-2 text-xs text-muted">
            {entry.sourceKind ? `From ${humanise(entry.sourceKind)}` : "Direct amendment"}
            {entry.lastAmendedAt && <> &middot; amended {stamp(entry.lastAmendedAt)}</>}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Mark tone={classificationTone(entry.classification)}>
            {classificationLabel(entry.classification)}
          </Mark>
          <Button
            tone="quiet"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? "Close history" : `${entry.revisions} ${entry.revisions === 1 ? "revision" : "revisions"}`}
          </Button>
        </div>
      </div>

      {open && (
        <MemoryDetail
          entry={entry}
          canManage={canManage}
          onChanged={onChanged}
        />
      )}
    </article>
  );
}

function MemoryDetail({
  entry,
  canManage,
  onChanged,
}: {
  entry: MemoryEntry;
  canManage: boolean;
  onChanged: () => void;
}) {
  const history = useResource<MemoryRevision[]>(
    () => api.get<MemoryRevision[]>(`/api/memory/${entry.id}/history`),
    [entry.id],
  );
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.body ?? "");
  const amend = useAction(async (body: string) => {
    await api.post<{ revisionId: string }>(`/api/memory/${entry.id}/revisions`, { body });
  });

  const submit = async () => {
    const ok = await amend.invoke(draft);
    if (ok) {
      setEditing(false);
      history.reload();
      onChanged();
    }
  };

  return (
    <div className="mt-4 border-t border-line pt-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="label">Revision history</p>
        {canManage && !editing && (
          <Button tone="secondary" onClick={() => setEditing(true)}>
            Add revision
          </Button>
        )}
      </div>

      {editing && (
        <Panel className="mb-4 p-4">
          <TextArea
            label="New complete wording"
            hint="The earlier wording stays in history. This adds a revision; it does not overwrite one."
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            className="min-h-36"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              tone="primary"
              loading={amend.pending}
              disabled={!draft.trim()}
              onClick={() => void submit()}
            >
              Add revision
            </Button>
            <Button tone="quiet" disabled={amend.pending} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
          {amend.refusal && <ActionRefusal refusal={amend.refusal} />}
        </Panel>
      )}

      {history.resource.state === "loading" && <SkeletonRows rows={2} />}
      {history.resource.state === "absent" && <AbsentNotice />}
      {history.resource.state === "failed" && (
        <Failed
          message={history.resource.message}
          correlationId={history.resource.correlationId}
          onRetry={history.reload}
        />
      )}
      {history.resource.state === "ready" && (
        <ol>
          {history.resource.data.map((revision, index) => (
            <li key={revision.id} className="register-entry py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-xs text-muted">
                  Revision <span data-numeric>{index + 1}</span> &middot; {revision.authorName}
                </p>
                <time className="text-2xs text-muted" dateTime={revision.createdAt}>
                  {stamp(revision.createdAt)}
                </time>
              </div>
              <p className="mt-1.5 whitespace-pre-wrap text-sm leading-[1.25rem] text-secondary">
                {revision.body}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ------------------------------------------------------------ arrivals ----

function ArrivalsRegion({
  engagementId,
  resource,
  workstreams,
  ceiling,
  onRetry,
  onChanged,
}: {
  engagementId: string;
  resource: Resource<SourceRecord[]>;
  workstreams: { id: string; name: string }[];
  ceiling: string;
  onRetry: () => void;
  onChanged: () => void;
}) {
  return (
    <>
      <Region title="Write a note">
        <ForwardComposer
          engagementId={engagementId}
          workstreams={workstreams}
          ceiling={ceiling}
          onChanged={onChanged}
        />
      </Region>

      <Region
        title="Notes on this engagement"
        count={resource.state === "ready" ? resource.data.length : undefined}
      >
        {resource.state === "loading" && <SkeletonRows rows={4} />}
        {resource.state === "absent" && <AbsentNotice />}
        {resource.state === "failed" && (
          <Failed
            message={resource.message}
            correlationId={resource.correlationId}
            onRetry={onRetry}
          />
        )}
        {resource.state === "ready" && resource.data.length === 0 && (
          <Empty title="Nothing written down yet">
            Anything that was said and matters: a call, a meeting, a message
            a client sent. It is kept exactly as it came, with who filed it
            and when, and nothing is edited away afterwards.
          </Empty>
        )}
        {resource.state === "ready" &&
          resource.data.map((source) => (
            <Arrival key={source.id} source={source} />
          ))}
      </Region>
    </>
  );
}

/**
 * One filed note, and the whole of it.
 *
 * `raw_text` was written on insert and returned by nothing, so a person
 * wrote a note — the entire purpose of this screen — and the platform
 * showed them a subject and a source label forever while the words
 * themselves were unreadable. A record you cannot read back is not a
 * record.
 *
 * Collapsed by default because a forwarded thread can be very long and
 * twenty of them expanded is not a list. One click opens it.
 */
function Arrival({ source }: { source: SourceRecord }) {
  const [open, setOpen] = useState(false);

  return (
            <article className="register-entry py-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {/* The subject leads, because it is the only thing that
                      tells one arrival from another. Where it came from is
                      supporting detail, not the headline it used to be.
                      Rows filed before 034 have no subject and fall back
                      to the old wording rather than rendering blank. */}
                  <p className="text-base text-ink">
                    {source.subject ?? humanise(source.kind)}
                  </p>
                  <p className="mt-1 text-xs text-muted">
                    {humanise(source.kind)}
                    {source.workstream && <> &middot; {source.workstream}</>}
                    &nbsp;&middot; filed by {source.submittedByName} &middot;{" "}
                    {stamp(source.submittedAt)}
                    {source.receivedAt && <> &middot; received {stamp(source.receivedAt)}</>}
                  </p>
                  {source.externalRef && (
                    <p className="mt-1 break-all font-mono text-2xs text-muted">
                      Reference {source.externalRef}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <Mark tone={classificationTone(source.classification)}>
                    {classificationLabel(source.classification)}
                  </Mark>
                  <Mark tone={source.proposals > 0 ? "live" : "rest"}>
                    {source.proposals} {source.proposals === 1 ? "proposal" : "proposals"}
                  </Mark>
                </div>
              </div>

              {source.rawText && (
                <>
                  <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    className="mt-2 text-sm text-secondary underline-offset-4 hover:text-ink hover:underline"
                  >
                    <span
                      aria-hidden
                      className={
                        "mr-1.5 inline-block font-mono transition-transform duration-150 " +
                        (open ? "rotate-90" : "")
                      }
                    >
                      {"\u203a"}
                    </span>
                    {open ? "Hide what was said" : "Read what was said"}
                  </button>
                  {/* Verbatim, preserving the line breaks it arrived with —
                      a pasted WhatsApp thread is unreadable reflowed. */}
                  <div hidden={!open}>
                    <pre className="mt-2 max-w-[70ch] whitespace-pre-wrap break-words border-l-2 border-line pl-4 font-sans text-sm text-ink">
                      {source.rawText}
                    </pre>
                  </div>
                </>
              )}
            </article>
  );
}

function ForwardComposer({
  engagementId,
  workstreams,
  ceiling,
  onChanged,
}: {
  engagementId: string;
  workstreams: { id: string; name: string }[];
  /** The highest classification this actor may file at. */
  ceiling: string;
  onChanged: () => void;
}) {
  const [kind, setKind] = useState<Exclude<IngestionKind, "otter">>("manual");
  const [subject, setSubject] = useState("");
  const [workstreamId, setWorkstreamId] = useState("");
  const [text, setText] = useState("");
  const [receivedAt, setReceivedAt] = useState("");
  const [externalRef, setExternalRef] = useState("");
  // Starts at the floor rather than at "internal", which a client-visible
  // ceiling would not allow. The select narrows to the ceiling anyway, so
  // starting low means the initial value is always offerable.
  const [classification, setClassification] =
    useState<Classification>("client_visible");
  const [result, setResult] = useState<IngestResult | null>(null);

  const forward = useAction(async () => {
    const next = await api.post<IngestResult>(`/api/engagements/${engagementId}/forward`, {
      kind,
      text,
      subject: subject.trim() || undefined,
      workstreamId: workstreamId || undefined,
      receivedAt: receivedAt ? new Date(receivedAt).toISOString() : undefined,
      externalRef: externalRef.trim() || undefined,
      classification,
    });
    setResult(next);
  });

  const submit = async () => {
    const ok = await forward.invoke();
    if (ok) {
      setText("");
      setSubject("");
      setReceivedAt("");
      setExternalRef("");
      // The workstream and the source are deliberately NOT cleared. You
      // file several things about one stream in a row; making the common
      // case re-pick both every time is how a form stops being used.
      onChanged();
    }
  };

  return (
    <Panel className="p-4 sm:p-5">
      <div className="grid gap-4 md:grid-cols-2">
        {/* Subject first, and full width. Every row in the list below used
            to read "WhatsApp · filed by X · 2 proposals" — identical to
            every other row — so twenty arrivals were twenty things you had
            to open to tell apart. A person writes this; extraction
            proposes meaning, it does not title things. */}
        <div className="md:col-span-2">
          <Field
            label="What this was about"
            hint="One line. This is what the list shows, so it is how anyone finds it again."
            maxLength={300}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder="Client confirmed the FY25 ledger is with their auditor"
          />
        </div>

        <Select
          label="How it arrived"
          value={kind}
          onChange={(event) => setKind(event.target.value as Exclude<IngestionKind, "otter">)}
        >
          <option value="manual">Note</option>
          <option value="call">Phone call</option>
          <option value="meeting">Meeting</option>
          <option value="site_visit">Site visit</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="telegram">Telegram</option>
          <option value="sms">SMS</option>
          <option value="email">Email</option>
          <option value="letter">Letter</option>
          <option value="document">Document</option>
        </Select>

        {/* The tag that makes intake part of the engagement rather than a
            separate application. Tasks, milestones, deliverables and RAID
            entries all hang off a workstream; this was the only thing that
            did not. Optional on purpose — a note worth keeping is worth
            keeping before anyone has decided where it belongs. */}
        <Select
          label="Which workstream"
          hint="Optional. Tagging it is how it turns up when somebody works on that stream."
          value={workstreamId}
          onChange={(event) => setWorkstreamId(event.target.value)}
        >
          <option value="">The engagement as a whole</option>
          {workstreams.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </Select>

        {/* Was offering all four to everybody. A consultant's ceiling is
            `internal`, so picking "Restricted" produced a bare "You do
            not have permission to do that" from the row-level security
            policy, with nothing to say that the classification was the
            objection. */}
        <ClassificationSelect
          value={classification}
          ceiling={ceiling}
          onChange={(v) => setClassification(v as Classification)}
        />

        <div className="md:col-span-2">
          <TextArea
            label="What was said"
            hint="Keep the original wording. Any suggested updates must be reviewed before they are added to the engagement record."
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Paste the message, or write what happened, exactly as it was."
            // Taller than it was: this is the body of the record, and a
            // three-line box tells people to write three lines.
            className="min-h-56"
          />
        </div>

        <Field
          label="When it was received"
          type="datetime-local"
          value={receivedAt}
          onChange={(event) => setReceivedAt(event.target.value)}
        />
        <Field
          label="External reference"
          value={externalRef}
          onChange={(event) => setExternalRef(event.target.value)}
          placeholder="Message id, thread or email reference"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          tone="primary"
          loading={forward.pending}
          disabled={!text.trim()}
          onClick={() => void submit()}
        >
          Add information
        </Button>
        <p className="max-w-[52ch] text-xs text-muted">
          This saves the original information and sends any suggested updates
          for review. It does not create a task, milestone or deliverable.
        </p>
      </div>

      {forward.refusal && <ActionRefusal refusal={forward.refusal} />}
      {result && (
        <p role="status" className="mt-4 border-t border-line pt-3 text-xs text-secondary">
          Filed. {result.proposed.length === 0
            ? "No updates were suggested; the source remains in recent information."
            : `${result.proposed.length} ${result.proposed.length === 1 ? "item is" : "items are"} waiting for review.`}
        </p>
      )}
    </Panel>
  );
}

// --------------------------------------------------------------- shared ----

function suggestionOf(value: unknown): Suggestion | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<Suggestion>;
  if (
    typeof candidate.kind !== "string" ||
    typeof candidate.quote !== "string" ||
    typeof candidate.summary !== "string" ||
    (candidate.confidence !== "low" && candidate.confidence !== "medium")
  ) {
    return null;
  }
  return candidate as Suggestion;
}

function ActionRefusal({
  refusal,
}: {
  refusal: { message: string; correlationId?: string };
}) {
  return (
    <p
      role="alert"
      className="mt-3 border border-danger/40 bg-danger/[0.08] px-3 py-2 text-xs text-danger"
    >
      {refusal.message}
      {refusal.correlationId && (
        <span className="ml-2 font-mono text-muted">Reference {refusal.correlationId}</span>
      )}
    </p>
  );
}
